"use client";

import { Dots, Text } from "@pamperedchef/design-system";
import type { MouseEvent } from "react";

import * as styles from "./LoadMore.module.scss";

export interface LoadMoreProps {
	loaded: number;
	total: number;
	/** The url of the next set, so the link works and is crawled without script. */
	href: string;
	isLoading?: boolean;
	/** Appends the next set in place; without it the link loads the next url. */
	onLoadMore?: () => void;
	label?: string;
}

function countLabel(loaded: number, total: number): string {
	return `Showing ${loaded} of ${total} ${total === 1 ? "result" : "results"}`;
}

export function LoadMore({
	loaded,
	total,
	href,
	isLoading = false,
	onLoadMore,
	label = "Load More"
}: LoadMoreProps): JSX.Element {
	const hasMore = loaded < total;

	function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
		if (!onLoadMore || event.metaKey || event.ctrlKey || event.shiftKey) {
			return;
		}
		event.preventDefault();
		if (!isLoading) onLoadMore();
	}

	return (
		<div className={styles["load-more"]} data-component="LoadMore">
			<Text
				tag="p"
				className={styles["load-more__count"]}
				aria-live="polite"
			>
				{countLabel(loaded, total)}
			</Text>
			{hasMore ? (
				<a
					href={href}
					rel="next"
					className={styles["load-more__link"]}
					aria-disabled={isLoading || undefined}
					aria-label={isLoading ? label : undefined}
					onClick={handleClick}
				>
					{isLoading ? <Dots aria-hidden /> : label}
				</a>
			) : null}
		</div>
	);
}
